import { createSignal, onMount } from 'solid-js';
import { View, Text } from "@lightningtv/solid";
import PinKeyboard from './PinKeyboard';
import styles from '../styles';

export default function SetPin(props: {
    onPinSet: (pin: string) => void;
    onSkip?: () => void; // Optional skip callback
    showBackground?: boolean; // Control whether to show full background
    showSkip?: boolean; // Control whether to show skip button
    x?: number; // Position when embedded
    y?: number; // Position when embedded
    inputDelay?: number; // Delay in milliseconds before accepting input (default 100ms)
    onBackLeft?: () => void; // Add this prop for back navigation
    keyboardRef?: any; // Add this prop to expose keyboard reference
    autoFocus?: boolean;
    isForFast?: string

}) {
    const [step, setStep] = createSignal<"set" | "confirm" | "success">("set");
    const [firstPin, setFirstPin] = createSignal("");
    const [canAcceptInput, setCanAcceptInput] = createSignal(false);
    const [currentPinLength, setCurrentPinLength] = createSignal(0); // Track current PIN length
    const keyboardRef: any = props.keyboardRef || {};
    let skipButtonRef: any;

    // Default to showing background and skip if not specified
    const showBackground = props.showBackground !== false;
    const showSkip = props.showSkip !== false && props.onSkip;
    const inputDelay = props.inputDelay || 100; // Default 100ms delay

    // Set up input delay when component mounts
    onMount(() => {
        setTimeout(() => {
            setCanAcceptInput(true);
        }, inputDelay);
    });

    // Function to handle PIN input changes (called from PinKeyboard)
    const handlePinChange = (pinLength: number) => {
        setCurrentPinLength(pinLength);

        // Auto-focus to Enter button when 4 characters are entered
        if (pinLength === 4) {
            // Focus the Enter button in the keyboard
            keyboardRef.focusDoneButton?.();
        }
    };

    const handlePinSubmit = (pin: string) => {
        // Only process input if delay has passed
        if (!canAcceptInput()) return;

        if (step() === "set") {
            setFirstPin(pin);
            setStep("confirm");
            setCurrentPinLength(0); // Reset length for confirm step
            keyboardRef.resetInput?.(); // clear after first pin
        } else if (step() === "confirm") {
            if (pin === firstPin()) {
                localStorage.setItem("userPin", pin);
                setStep("success");
                setCurrentPinLength(0); // Reset length
                keyboardRef.resetInput?.(); // clear after confirm

                // Show success message for 2 seconds, then call onPinSet
                setTimeout(() => {
                    props.onPinSet(pin);
                    setStep("set");
                    setFirstPin("");
                    setCurrentPinLength(0); // Reset length
                }, props.autoFocus === true || props.isForFast === "true" ? 0 : 2000);

            } else {
                // Pins don't match, go back to first step
                setStep("set");
                setFirstPin("");
                setCurrentPinLength(0); // Reset length
                keyboardRef.resetInput?.(); // clear after mismatch
            }
        }
    };

    const handleSkip = () => {
        // Only process skip if delay has passed
        if (!canAcceptInput()) return;

        if (props.onSkip) {
            props.onSkip();
        }
    };

    // Determine if skip button should be shown
    const shouldShowSkip = () => {
        return showSkip && step() === "set" && currentPinLength() < 4;
    };

    // Keyboard-only version for embedding
    const KeyboardOnlyContent = () => (
        <View x={props.x || 0} y={props.y || 0}>
            <Text x={200} y={0} fontSize={36}>
                {step() === "set" ? "Create your PIN" : "Confirm Your PIN"}
            </Text>

            {/* Show success message */}
            {step() === "success" && (
                <View x={200} y={50}>
                    <Text fontSize={32} color="#28a745">
                        PIN Set Successfully!
                    </Text>
                </View>
            )}

            {/* Only render keyboard if input is allowed and not in success state */}
            {canAcceptInput() && step() !== "success" ? (
                <PinKeyboard
                    onSubmit={handlePinSubmit}
                    onPinChange={handlePinChange} // Pass the PIN change handler
                    keyboardRef={keyboardRef}
                    onDown={shouldShowSkip() ? () => skipButtonRef?.setFocus?.() : undefined}
                    onBackLeft={props.onBackLeft} // Pass the onBackLeft prop
                    autoFocus={props.autoFocus ? props.autoFocus : false}
                />
            ) : step() !== "success" ? (
                // Show placeholder or disabled keyboard during delay
                <View y={100}>
                    <Text x={200} y={50} fontSize={24} color={"#666666"}>
                        Initializing PIN setup...
                    </Text>
                </View>
            ) : null}

            {/* Skip button - only show if enabled, on first step, and PIN length < 4 */}
            {/* {shouldShowSkip() && (
                <View
                    ref={skipButtonRef}
                    style={styles.skipButton}
                    onEnter={canAcceptInput() ? handleSkip : undefined}
                    onUp={() => {
                        if (canAcceptInput()) {
                            keyboardRef.setFocus?.();
                        }
                    }}
                >
                    <Text
                        x={20}
                        y={20}
                        fontSize={24}
                        color={canAcceptInput() ? "#ffffff" : "#666666"}
                        textAlign={"center"}
                    >
                        {canAcceptInput() ? "Skip PIN Setup" : "Please wait..."}
                    </Text>
                </View>
            )} */}
        </View>
    );

    // Full background version
    const FullScreenContent = () => (
        <View color={"#182027"}>
            <View src={"https://i.ibb.co/Lzk5hD4Y/Maskgroup.png"}>
                <View src="https://i.ibb.co/ZVmDwNg/logo.png" x={800} y={50} width={270} height={82} />

                <View x={50} y={50}>
                    <View src="https://i.ibb.co/LX13T2JW/Group-Live-Tv.png" width={102} height={102} />
                    <Text x={120} y={33} fontSize={34} fontWeight={"bold"}></Text>
                </View>

                <View x={600} y={200}>
                    <KeyboardOnlyContent />
                </View>
            </View>
        </View>
    );

    return showBackground ? <FullScreenContent /> : <KeyboardOnlyContent />;
}